<template>
  <div class="filter-field-wrapper" :data-field="field">
    <label class="filter-label">
      <span class="label-text">{{ $t(field) }}</span>
      <span v-if="isMultiSelect" class="multi-badge">{{ $t('multiple') }}</span>
    </label>
    <div class="select-wrapper" :class="{ 'disabled': isDisabled && !isLoading, 'has-value': hasValue }">
      <Select
        class="filter-select"
        :multiple="isMultiSelect"
        :value="initialValue"
        @input="emitInput"
        :disabled="isDisabled"
        :key="field"
        :title="getTooltipText"
      >
        <SearchBar
          v-if="isSearchable"
          class="filter-search-bar"
          v-model="searchQuery"
        />
        <Option
          v-for="(item, index) in options"
          :value="item.id ? item.id : item"
          :label="optionLabel(item)"
          :key="index"
        />
      </Select>
      <div v-if="isDisabled && !isLoading" class="disabled-overlay">
        <svg class="lock-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="disabled-text">{{ getHelpText }}</span>
      </div>
    </div>
    <div v-if="showHelpText && !isDisabled" class="help-hint">
      <svg class="hint-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="8" r="1" fill="currentColor"/>
      </svg>
      <span>{{ getTooltipText }}</span>
    </div>
  </div>
</template>

<script>
import Select from '../Select';
import Option from '../Option';
import SearchBar from '../SearchBar';
import { mapGetters } from 'vuex';

export default {
  props: {
    field: { type: String, required: true },
    value: { type: [Array, String, Number], required: true }
  },
  data: () => ({
    initialValue: null,
    searchQuery: ''
  }),
  mounted() {
    // Initialize from store
    const selectedValue = this.filterSelected?.[this.field];
    if (selectedValue !== null && selectedValue !== undefined) {
      this.initialValue = Array.isArray(selectedValue) ? [...selectedValue] : selectedValue;
    } else {
      this.initialValue = this.isMultiSelect ? [] : null;
    }
  },
  methods: {
    optionLabel(item) {
      const name = item && item.name != null ? item.name : item;
      if (this.field === 'gender') return this.$roomTypeLabel(name);
      return name;
    },
    emitInput(value) {
      // Always allow user input - don't block based on _isUpdating
      // _isUpdating is only for preventing watcher loops, not blocking user actions
      this.$emit(this.isMultiSelect ? 'multiSelect' : 'select', value, this.field);
    },
    hasRoomsSelected() {
      const rooms = this.filterSelected?.rooms;
      if (!rooms) return false;
      if (Array.isArray(rooms)) return rooms.length > 0;
      return rooms !== null && rooms !== undefined;
    }
  },
  computed: {
    ...mapGetters(['filterSelected', 'isLoading']),
    options() {
      if (!this.value || !Array.isArray(this.value)) {
        return [];
      }
      
      let list = this.value;
      if (this.field === 'days') {
        list = this.value
          .filter(a => a !== null && a !== undefined && a !== '')
          .map(a => ({ id: a, name: this.$t(`weekMap[${a}]`) }))
          .filter(item => item.name && item.name.trim() !== '');
      }

      if (!this.isSearchable || !this.searchQuery.trim()) {
        return list;
      }

      const searchStr = this.searchQuery.trim().toLowerCase();
      const selectedIds = new Set(
        (Array.isArray(this.initialValue) ? this.initialValue : this.initialValue != null ? [this.initialValue] : [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      );
      return list.filter((item) => {
        const id = item && item.id != null ? item.id : item;
        const name = String(this.optionLabel(item)).toLowerCase();
        return name.includes(searchStr) || selectedIds.has(Number(id));
      });
    },
    isSearchable() {
      return this.field === 'cleaners';
    },
    isMultiSelect() {
      return this.field !== 'hour'
    },
    isDisabled() {
      if (this.isLoading) return true;
      if (this.field === 'floor' && !this.filterSelected.building) return true;
      if (this.field === 'gender' && this.hasRoomsSelected()) return true;
      if (this.field === 'building' && this.hasRoomsSelected()) return true;
      return false;
    },
    hasValue() {
      const value = this.filterSelected?.[this.field];
      return value !== null && value !== undefined && 
             (Array.isArray(value) ? value.length > 0 : value !== '');
    },
    showHelpText() {
      return !this.isDisabled && this.isMultiSelect && !this.isLoading;
    },
    getHelpText() {
      if (this.field === 'floor' && !this.filterSelected.building) {
        return this.$t('select_building_first');
      }
      if (this.field === 'gender' && this.hasRoomsSelected()) {
        return this.$t('cannot_select_gender_with_rooms');
      }
      if (this.field === 'building' && this.hasRoomsSelected()) {
        return this.$t('cannot_select_building_with_rooms');
      }
      return '';
    },
    getTooltipText() {
      if (this.field === 'floor' && !this.filterSelected.building) {
        return this.$t('select_building_first');
      }
      if (this.field === 'gender' && this.hasRoomsSelected()) {
        return this.$t('cannot_select_gender_with_rooms_tooltip');
      }
      if (this.field === 'building' && this.hasRoomsSelected()) {
        return this.$t('cannot_select_building_with_rooms_tooltip');
      }
      if (this.isMultiSelect) {
        return this.$t('multi_select_hint');
      }
      return '';
    },
  },
  watch: {
    field() {
      this.searchQuery = ''
    },
    value() {
      this.searchQuery = ''
    },
    // Watch filterSelected but only react to changes in this specific field
    filterSelected: {
      handler(newVal) {
        const newFieldValue = newVal?.[this.field];
        const currentValue = this.initialValue;
        
        // Check if value actually changed
        let valuesEqual = false;
        
        if (newFieldValue === null || newFieldValue === undefined) {
          valuesEqual = (currentValue === null || currentValue === undefined || 
                        (Array.isArray(currentValue) && currentValue.length === 0));
        } else if (Array.isArray(newFieldValue) && Array.isArray(currentValue)) {
          // Compare arrays by content
          const newSorted = [...newFieldValue].sort();
          const currentSorted = [...currentValue].sort();
          valuesEqual = newSorted.length === currentSorted.length && 
                       newSorted.every((val, idx) => val === currentSorted[idx]);
        } else {
          valuesEqual = newFieldValue === currentValue;
        }
        
        // Only update if value actually changed
        if (!valuesEqual) {
          if (newFieldValue === null || newFieldValue === undefined) {
            this.initialValue = this.isMultiSelect ? [] : null;
          } else if (Array.isArray(newFieldValue)) {
            this.initialValue = [...newFieldValue];
          } else {
            this.initialValue = newFieldValue;
          }
        }
      },
      deep: true,
      immediate: false
    }
  },
  components: { Select, Option, SearchBar }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

// Match filter-fixes.scss so scoped ::v-deep does not force a fixed 300px on narrow viewports
$vm-menu-min-width: #{"min(300px, calc(100vw - 24px))"};
$vm-menu-max-width: #{"min(500px, calc(100vw - 16px))"};

.filter-field-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  
  .label-text {
    text-transform: capitalize;
    letter-spacing: -0.01em;
  }
  
  .multi-badge {
    font-size: 10px;
    font-weight: 500;
    padding: 2px 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.select-wrapper {
  position: relative;
  transition: all 0.2s ease;
  
  &.has-value {
    .filter-select {
      border-color: #667eea;
      background: #f8faff;
    }
  }
  
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    .filter-select {
      background: #f3f4f6;
      border-color: #d1d5db;
    }
  }
}

.filter-select {
  @include border(#e5e7eb);
  height: 44px;
  width: 100% !important;
  border-radius: 10px;
  padding: 0 16px;
  margin: 0;
  transition: all 0.2s ease;
  background: white;
  font-size: 14px;
  color: #1f2937;
  
  &:hover:not(:disabled) {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
}

.disabled-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(243, 244, 246, 0.8);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  pointer-events: none;
  backdrop-filter: blur(2px);
  
  .lock-icon {
    width: 20px;
    height: 20px;
    stroke: #9ca3af;
  }
  
  .disabled-text {
    font-size: 11px;
    color: #6b7280;
    text-align: center;
    line-height: 1.3;
    max-width: 100%;
  }
}

.help-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  
  .hint-icon {
    width: 14px;
    height: 14px;
    stroke: #9ca3af;
    flex-shrink: 0;
  }
  
  span {
    line-height: 1.4;
  }
}

// Override Select component styles
::v-deep .md-field {
  margin: 0 !important;
  min-height: unset !important;
  
  .md-input {
    padding: 0 !important;
    height: 44px !important;
    line-height: 44px !important;
    font-size: 14px !important;
  }
}

// Z-index is handled globally in filter-fixes.scss
// Only keep min-width/max-width for FilterSelect component
::v-deep .md-menu-content {
  min-width: $vm-menu-min-width !important;
  max-width: $vm-menu-max-width !important;
  width: auto !important;
}

::v-deep .md-select-menu {
  min-width: $vm-menu-min-width !important;
  max-width: $vm-menu-max-width !important;
  width: auto !important;
}

// Fix text truncation in dropdown options
::v-deep .md-menu-content-container {
  min-width: $vm-menu-min-width !important;
  max-width: $vm-menu-max-width !important;
  width: auto !important;
}

// Don't override spacing - let vendor.scss handle it
// Only ensure text wrapping works
::v-deep .md-list-item {
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  
  .md-list-item-text {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    overflow: visible !important;
    text-overflow: unset !important;
  }
  
  .md-list-item-container {
    overflow: visible !important;
  }
}

// Ensure checkbox doesn't cause truncation
::v-deep .md-checkbox {
  flex-shrink: 0 !important;
  margin-inline-end: 12px !important;
}
</style>
